import React, { ReactElement, useState } from 'react';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { clearError } from 'commons/src/actions/requestActions';
import { analyticsLogger } from 'commons/src/analytics';
import { SPACE_DEVICE_SETTINGS, SPACE_EXPANDED_SENSORS } from 'commons/src/analytics/AnalyticsEvents';
import { getValidLanguageTagForMoment, mapDeviceType } from 'commons/src/commonFunctions';
import AccordionButton from 'commons/src/components/buttons/AccordionButton';
import DropdownOptions from 'commons/src/components/dropdown/DropdownOptions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import DeviceStatusPill from 'commons/src/components/pills/DeviceStatusPill';
import BatteryComponent from 'commons/src/components/sensors/BatteryComponent';
import RssiComponent from 'commons/src/components/sensors/RssiComponent';
import SpaceSensorValue from 'commons/src/components/sensors/SpaceSensorValue';
import { roleRestrictions } from 'commons/src/constants';
import { CurrentSensorValuesType } from 'commons/src/models/commonTypeScript';
import DeviceHealthStatus from 'commons/src/models/deviceHealthStatus';
import { MenuItem } from 'commons/src/models/menuModels';
import DeviceIcon from '../../../../img/icon_device';
import { IndoorSpace, SpaceDevice } from '../../../../models/spaceModels';
import { BusinessRequestType as RequestType } from '../../../../reducers/BusinessRequestType';
import SpaceMoveDeviceModal from '../moveOrRemoveDevice/SpaceMoveDeviceModal';
import SpaceRemoveDeviceModal from '../moveOrRemoveDevice/SpaceRemoveDeviceModal';
import styles from './SpaceDevicesElement.module.scss';

export type Props = {
    space: IndoorSpace;
    device: SpaceDevice;
};

const SpaceDevicesElement = ({ space, device }: Props): React.ReactElement => {
    const { serialNumber, segmentName } = device;

    const { t: txt } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { buildingId: locationId = '' } = useParams();

    const [deleteModalOpened, setDeleteModalOpened] = useState(false);
    const [moveModalOpened, setMoveModalOpened] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const redirectToSettings = (): void => {
        const settingsUrl = generatePath('/devices/:serialNumber/change-location', { serialNumber });
        analyticsLogger(SPACE_DEVICE_SETTINGS, {
            spaceId: space.id,
            locationId,
            serialNumber: device.serialNumber,
        });
        navigate(settingsUrl);
    };

    const onRemoveOptionHandler = (): void => {
        dispatch(clearError(RequestType.RemoveDeviceFromSpace));
        setDeleteModalOpened(true);
    };

    const deviceDropdownOptions: MenuItem[] = [
        {
            onClick: redirectToSettings,
            text: txt('Settings'),
            requiredRoleLevel: roleRestrictions.editSpace,
            disabled: false,
            requiredGroupTypes: [],
        },
        {
            onClick: () => setMoveModalOpened(true),
            text: txt('Move'),
            requiredRoleLevel: roleRestrictions.editSpace,
            disabled: false,
            requiredGroupTypes: [],
        },
        {
            onClick: onRemoveOptionHandler,
            text: txt('Remove'),
            requiredRoleLevel: roleRestrictions.editSpace,
            disabled: false,
            requiredGroupTypes: [],
        },
    ];

    const setAccordionExpanded = (): void => {
        setIsExpanded(!isExpanded);
        analyticsLogger(SPACE_EXPANDED_SENSORS, {
            spaceId: space.id,
            locationId,
            serialNumber: device.serialNumber,
        });
    };

    const neverSyncedSection = (): ReactElement => {
        const language: string = getValidLanguageTagForMoment();
        const lastSynced = device.lastSynced
            ? moment.utc(device.lastSynced).local().locale(language).fromNow()
            : txt('neverSynced');

        return (
            <div className={styles.lastSynced}>
                <span>{txt('LastSyncedNoSemicolon')}</span>
                <span>{lastSynced}</span>
            </div>
        );
    };

    const currentValueBlock = (currentValues: CurrentSensorValuesType[]): ReactElement[] =>
        currentValues.map(currentValue => (
            <div className={styles.currentValueBlock} key={currentValue.type}>
                <SpaceSensorValue
                    value={currentValue.value}
                    unit={currentValue.unit}
                    type={currentValue.type}
                    thresholds={currentValue.thresholds}
                    showSensorType
                    key={currentValue.type}
                    healthStatus={device.healthStatus}
                />
            </div>
        ));

    const currentValuesSection = (): ReactElement | null => {
        const currentSensorValues = device.currentSensorValues || [];

        if (currentSensorValues.length === 0) {
            return null;
        }

        const mainCurrentValues: CurrentSensorValuesType[] = isExpanded
            ? currentSensorValues
            : currentSensorValues.slice(0, 6);

        return (
            <div className={styles.deviceValuesRow}>
                <div className={styles.currentValues}>{currentValueBlock(mainCurrentValues)}</div>
                {currentSensorValues.length > 6 && (
                    <div className={styles.accordionButton}>
                        <AccordionButton
                            title={txt(isExpanded ? 'ViewLess' : 'ViewMore')}
                            onClick={setAccordionExpanded}
                            expanded={isExpanded}
                            testId="expand-button"
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <li key={serialNumber} id={serialNumber} className={styles.body} data-space-device={device.serialNumber}>
                <div className={styles.deviceTopBar}>
                    <div className={styles.deviceInfo}>
                        <div className={styles.deviceNameAndSerial}>
                            <div className={styles.iconAndName}>
                                <DeviceIcon className={styles.deviceIcon} />
                                <div className={styles.name}>{segmentName}</div>
                            </div>
                            <div className={styles.serialNumber}>
                                ({txt(`${mapDeviceType(device.deviceType)}FullName`)} - {device.serialNumber})
                            </div>
                        </div>
                        {[DeviceHealthStatus.notSynced, DeviceHealthStatus.offline].includes(device.healthStatus) && (
                            <div className={styles.status}>
                                <DeviceStatusPill status={device.healthStatus} deviceType="device" />
                            </div>
                        )}
                        {device.healthStatus !== DeviceHealthStatus.notSynced && neverSyncedSection()}
                        <BatteryComponent
                            batteryPercentage={device.batteryPercentage}
                            healthStatus={device.healthStatus}
                        />
                        <RssiComponent rssi={device.rssi} healthStatus={device.healthStatus} />
                    </div>
                    <DropdownOptions
                        options={deviceDropdownOptions}
                        id={`device_options_${serialNumber}`}
                        dotMenu
                        customButton={<MaterialIcon extraClass={styles.dots} name="more_horiz" />}
                    />
                </div>
                {currentValuesSection()}
            </li>
            <SpaceRemoveDeviceModal
                device={device}
                visible={deleteModalOpened}
                hide={(): void => setDeleteModalOpened(false)}
                payload={{ locationId, spaceId: space.id, serialNumber }}
            />
            <SpaceMoveDeviceModal
                device={device}
                isOpen={moveModalOpened}
                closeModal={(): void => setMoveModalOpened(false)}
                locationId={locationId}
                space={space}
            />
        </>
    );
};

export default SpaceDevicesElement;
